'use client';

import { cn } from '@savvy-kit/lib/utils';
import { motion } from 'motion/react';

import { bezier } from './grading';
import { PULSE } from './tokens';
import { useMotionReduced } from './use-motion-reduced';

type PulseRingProps = {
  /** Any CSS colour; defaults to the badge's own text colour. */
  readonly tone?: string;
  /** Overdue and the like: breathes faster and stronger. */
  readonly urgent?: boolean;
  readonly intensity?: number;
  readonly duration?: number;
  readonly className?: string;
};

/**
 * A ring breathing out of a circular badge, near-invisible on purpose: it says "still open" out of the corner
 * of the eye without pulling focus. Sits behind its parent's content, so the icon stays crisp; the parent
 * needs `position: relative`.
 */
function PulseRing({
  tone = 'currentColor',
  urgent = false,
  intensity = urgent ? PULSE.intensityUrgent : 1,
  duration = urgent ? PULSE.durationUrgent : PULSE.duration,
  className,
}: PulseRingProps) {
  const reduced = useMotionReduced();
  if (reduced) return null;
  return (
    <motion.span
      data-slot="pulse-ring"
      aria-hidden
      className={cn('pointer-events-none absolute inset-0 rounded-full', className)}
      style={{ border: `1.5px solid ${tone}` }}
      initial={{ scale: 1, opacity: 0 }}
      animate={{ scale: [...PULSE.scale], opacity: [0, PULSE.alpha * intensity, 0] }}
      transition={{
        duration,
        repeat: Infinity,
        times: [0, 0.45, 1],
        ease: bezier(PULSE.ease),
        repeatDelay: PULSE.repeatDelay,
      }}
    />
  );
}

export { PulseRing };
export type { PulseRingProps };
